'use client';

import React, { useState } from 'react';
import FlightSearchForm from './FlightSearchForm';
import HotelSearchForm from './HotelSearchForm';
import PackageSearchForm from './PackageSearchForm';
import Icon from '@/components/ui/AppIcon';

const tabs = [
  { id: 'flights', label: 'Flights', icon: 'PaperAirplaneIcon' },
  { id: 'hotels', label: 'Hotels', icon: 'BuildingOffice2Icon' },
  { id: 'packages', label: 'Packages', icon: 'GiftIcon' },
];

export default function HeroSection() {
  const [activeTab, setActiveTab] = useState<'flights' | 'hotels' | 'packages'>('flights');

  return (
    <section className="relative min-h-[620px] hero-gradient flex flex-col items-center justify-center pt-16 pb-12 overflow-hidden">
      {/* Background texture */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
          }}
        />
      </div>

      {/* Floating orb decoration */}
      <div className="absolute top-20 right-10 w-64 h-64 rounded-full bg-accent/20 blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 left-10 w-48 h-48 rounded-full bg-white/10 blur-2xl pointer-events-none" />

      <div className="relative z-10 w-full max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16">
        {/* Hero Copy */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-white/15 text-white text-sm font-medium px-4 py-1.5 rounded-full mb-4 border border-white/20">
            <Icon name="SparklesIcon" size={14} />
            <span>500M+ prices checked daily across 1,200+ partners</span>
          </div>
          <h1 className="text-hero-xl text-white mb-3">
            Find Your Perfect Trip,<br />
            <span className="text-accent" style={{ filter: 'brightness(1.3)' }}>
              Pay Less for It
            </span>
          </h1>
          <p className="text-hero-sub text-white/80 max-w-xl mx-auto">
            Compare flights, hotels, and vacation packages from hundreds of travel sites — all in one search.
          </p>
        </div>

        {/* Search Panel */}
        <div className="bg-card rounded-2xl search-panel-shadow max-w-5xl mx-auto overflow-hidden">
          {/* Tab Bar */}
          <div className="flex items-center gap-1 p-3 bg-primary">
            {tabs.map((tab) => (
              <button
                key={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold transition-all duration-150 ${
                  activeTab === tab.id ? 'tab-active' : 'tab-inactive'
                }`}
              >
                <Icon name={tab.icon as any} size={16} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Form Content */}
          <div className="p-5">
            {activeTab === 'flights' && <FlightSearchForm />}
            {activeTab === 'hotels' && <HotelSearchForm />}
            {activeTab === 'packages' && <PackageSearchForm />}
          </div>
        </div>

        {/* Quick trust line */}
        <div className="flex flex-wrap items-center justify-center gap-6 mt-6">
          {[
            { icon: 'ShieldCheckIcon', text: 'No hidden fees' },
            { icon: 'ArrowPathIcon', text: 'Free price alerts' },
            { icon: 'LockClosedIcon', text: 'Secure & private' },
          ].map((item) => (
            <div key={`trust-${item.text}`} className="flex items-center gap-2 text-white/80 text-sm">
              <Icon name={item.icon as any} size={16} className="text-accent" style={{ filter: 'brightness(1.4)' }} />
              <span>{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}